/**
 * @typedef {typeof window & { Swiper: any }} WindowWithSwiper
 */

/**
 * Swiper Carousel Component
 *
 * A generic, reusable carousel component that initializes Swiper with configurations
 * from data attributes or direct config objects. Supports auto-initialization,
 * manual initialization, and proper cleanup.
 *
 * @example
 * <!-- HTML with data-config -->
 * <div class="swiper__carousel" data-config="myCarouselConfig">
 *   <div class="swiper-wrapper">
 *     <div class="swiper-slide">Slide 1</div>
 *   </div>
 * </div>
 *
 * <script>
 *   window.myCarouselConfig = {
 *     slidesPerView: 1,
 *     autoplay: { delay: 5000 },
 *   };
 * </script>
 *
 * @example
 * // Manual initialization with direct config
 * import { CarouselComponent } from './carousel.js';
 * const carousel = new CarouselComponent(element, { slidesPerView: 3 });
 * carousel.init();
 *
 * @example
 * // Using the helper function
 * import { initCarousel } from './carousel.js';
 * const carousel = initCarousel(element, { slidesPerView: 2 });
 */
class CarouselComponent {
  /**
   * @type {any}
   */
  swiper = null;

  /**
   * @type {HTMLElement}
   */
  element;

  /**
   * @type {Object | undefined}
   */
  config;

  /**
   * @type {number}
   */
  swiperLoadRetries = 0;

  /**
   * @type {number}
   */
  maxRetries = 10;

  /**
   * Create a new CarouselComponent instance
   * @param {HTMLElement} element - The swiper container element
   * @param {Object} [config] - Optional Swiper configuration override
   */
  constructor(element, config) {
    if (!element) {
      throw new Error("CarouselComponent requires a valid element");
    }
    this.element = element;
    this.config = config;
  }

  /**
   * Initialize the carousel with retry logic for Swiper loading
   * @returns {any | undefined} Swiper instance
   */
  init() {
    const w = /** @type {WindowWithSwiper} */ (window);

    // Check if Swiper is loaded, with retry logic
    if (!w.Swiper) {
      if (this.swiperLoadRetries < this.maxRetries) {
        this.swiperLoadRetries++;
        setTimeout(() => this.init(), 100);
        return;
      } else {
        console.error(
          "Swiper library failed to load after multiple retries. Please ensure Swiper is included in your theme.",
          this.element
        );
        return;
      }
    }

    // Skip if already initialized
    if (this.element.classList.contains("swiper-initialized")) {
      return this.swiper;
    }

    // Get configuration
    const configObject = this.getConfig();

    if (!configObject) {
      console.warn(
        "No configuration found for carousel. Please set data-config attribute or provide a config object.",
        this.element
      );
      return;
    }

    try {
      // Check if loop should be disabled due to insufficient slides
      // This must happen BEFORE Swiper initialization to prevent the warning
      if (configObject && /** @type {any} */ (configObject).loop) {
        const slides = this.element.querySelectorAll(".swiper-slide");
        const slideCount = slides.length;

        if (slideCount === 0) {
          // No slides at all, definitely disable loop
          /** @type {any} */ (configObject).loop = false;
        } else {
          // Get the maximum slidesPerView and slidesPerGroup from config and breakpoints
          let maxSlidesPerView = 1;
          let maxSlidesPerGroup = 1;
          const config = /** @type {any} */ (configObject);
          const slidesPerView = config.slidesPerView || 1;
          const slidesPerGroup = config.slidesPerGroup || 1;

          // Handle different slidesPerView types
          if (typeof slidesPerView === "number") {
            maxSlidesPerView = Math.max(maxSlidesPerView, Math.ceil(slidesPerView));
          } else if (slidesPerView === "auto") {
            // For 'auto', we need at least 4 slides for loop to work properly
            if (slideCount < 4) {
              config.loop = false;
            }
          }

          if (typeof slidesPerGroup === "number") {
            maxSlidesPerGroup = Math.max(maxSlidesPerGroup, Math.ceil(slidesPerGroup));
          }

          // Check breakpoints for maximum slidesPerView and slidesPerGroup
          if (config.breakpoints && typeof config.breakpoints === "object") {
            for (const breakpoint of Object.values(config.breakpoints)) {
              if (breakpoint && typeof breakpoint === "object") {
                const bp = /** @type {any} */ (breakpoint);
                const bpSlidesPerView = bp.slidesPerView;
                const bpSlidesPerGroup = bp.slidesPerGroup;
                if (typeof bpSlidesPerView === "number") {
                  maxSlidesPerView = Math.max(maxSlidesPerView, Math.ceil(bpSlidesPerView));
                }
                if (typeof bpSlidesPerGroup === "number") {
                  maxSlidesPerGroup = Math.max(maxSlidesPerGroup, Math.ceil(bpSlidesPerGroup));
                }
              }
            }
          }

          // Swiper loop requires at least max(slidesPerView, slidesPerGroup) * 2 slides to work properly
          // Use Math.ceil to handle decimal values (e.g., 1.2 becomes 2)
          const requiredMultiplier = Math.max(maxSlidesPerView, maxSlidesPerGroup);
          const minRequiredSlides = requiredMultiplier * 2;

          if (slideCount < minRequiredSlides) {
            config.loop = false;
          }
        }
      }

      // Convert pagination selector to DOM element if it's a string (helps when element is in hidden container)
      // Reuse config variable from loop check above, or create it if loop check didn't run
      const swiperConfig = /** @type {any} */ (configObject);
      if (swiperConfig && swiperConfig.pagination && typeof swiperConfig.pagination.el === "string") {
        const paginationEl = document.querySelector(swiperConfig.pagination.el);
        if (paginationEl) {
          swiperConfig.pagination.el = paginationEl;
        }
      }

      // Convert navigation selectors to DOM elements if they're strings
      if (swiperConfig && swiperConfig.navigation) {
        if (typeof swiperConfig.navigation.nextEl === "string") {
          const nextEl = document.querySelector(swiperConfig.navigation.nextEl);
          if (nextEl) {
            swiperConfig.navigation.nextEl = nextEl;
          }
        }
        if (typeof swiperConfig.navigation.prevEl === "string") {
          const prevEl = document.querySelector(swiperConfig.navigation.prevEl);
          if (prevEl) {
            swiperConfig.navigation.prevEl = prevEl;
          }
        }
      }

      // Initialize Swiper
      this.swiper = new w.Swiper(this.element, configObject);

      // Double-check after initialization: disable loop if Swiper still has it enabled but there aren't enough slides
      // Use realIndex to get the actual number of unique slides (not duplicated ones)
      if (this.swiper && this.swiper.params && this.swiper.params.loop) {
        // Get the real slide count (before duplication for loop)
        const realSlideCount = this.swiper.slides
          ? this.swiper.virtual
            ? this.swiper.virtual.slides.length
            : this.swiper.slides.length
          : 0;

        // If Swiper duplicated slides for loop, we need to divide by the duplication factor
        // But it's safer to check the original slides from the DOM
        const originalSlides = this.element.querySelectorAll(".swiper-slide");
        const actualSlideCount = originalSlides.length;

        const currentSlidesPerView = this.swiper.params.slidesPerView || 1;
        const currentSlidesPerGroup = this.swiper.params.slidesPerGroup || 1;
        const slidesPerViewNum =
          typeof currentSlidesPerView === "number"
            ? Math.ceil(currentSlidesPerView)
            : currentSlidesPerView === "auto"
              ? 2
              : 1;
        const slidesPerGroupNum = typeof currentSlidesPerGroup === "number" ? Math.ceil(currentSlidesPerGroup) : 1;

        // Swiper needs at least max(slidesPerView, slidesPerGroup) * 2 slides for loop
        const requiredMultiplier = Math.max(slidesPerViewNum, slidesPerGroupNum);
        const minRequiredSlides = requiredMultiplier * 2;

        if (actualSlideCount < minRequiredSlides) {
          this.swiper.params.loop = false;
          // Update to apply the change
          this.swiper.update();
        }
      }

      // Store swiper instance for external access
      if (configObject) {
        configObject.$swiper = this.swiper;
      }

      // Store instance on element for easy access
      this.element._carouselInstance = this;

      // Automatically attach navigation visibility handlers if navigation is configured
      if (configObject && configObject.navigation) {
        const prevEl = configObject.navigation.prevEl;
        const nextEl = configObject.navigation.nextEl;
        if (prevEl && nextEl) {
          attachNavigationVisibilityHandlers(this.swiper, prevEl, nextEl);
        }
      }

      // Verify and fix pagination if it wasn't created (e.g., when carousel is in hidden container)
      const paginationConfig = /** @type {any} */ (configObject);
      if (
        paginationConfig &&
        paginationConfig.pagination &&
        paginationConfig.pagination.el &&
        this.swiper &&
        this.swiper.pagination
      ) {
        const paginationEl =
          typeof paginationConfig.pagination.el === "string"
            ? document.querySelector(paginationConfig.pagination.el)
            : paginationConfig.pagination.el;

        if (paginationEl) {
          // Check if pagination bullets were created
          const bullets = paginationEl.querySelectorAll(".swiper-pagination-bullet");
          if (bullets.length === 0 && this.swiper.slides && this.swiper.slides.length > 0) {
            // Pagination element exists but bullets weren't created - manually render pagination
            if (this.swiper.pagination && typeof this.swiper.pagination.render === "function") {
              this.swiper.pagination.render();
              this.swiper.pagination.update();
            }
          }
        }
      }

      return this.swiper;
    } catch (error) {
      console.error("Failed to initialize Swiper carousel:", error, this.element);
      return undefined;
    }
  }

  /**
   * Get configuration from data attribute or constructor
   * @returns {Object | undefined} Swiper configuration
   */
  getConfig() {
    let customConfig;

    // Use constructor config if provided
    if (this.config) {
      customConfig = this.config;
    } else {
      // Otherwise, look for data-config attribute
      const dataConfigName = this.element.dataset.config;
      if (dataConfigName) {
        const w = /** @type {WindowWithSwiper} */ (window);
        if (dataConfigName in w) {
          customConfig = /** @type {any} */ (w)[dataConfigName];
        }
      }
    }

    if (!customConfig) {
      return undefined;
    }

    // If config explicitly opts out of defaults, return as-is
    if (customConfig.useDefault === false) {
      return customConfig;
    }

    // Extract config_id from config name pattern or explicit setting
    // Pattern: "nameConfig_id" or "nameConfigId" -> extract id
    let configId = customConfig.configId || this.element.dataset.configId;
    if (!configId && this.element.dataset.config) {
      const configName = this.element.dataset.config;
      // Try to extract from patterns like "reviewsCarouselConfig_abc123" or "heroConfig_xyz"
      const match = configName.match(/_([a-zA-Z0-9_-]+)$/);
      if (match) {
        configId = match[1];
      }
    }
    configId = configId || "default";

    // Extract carousel name from element classes or explicit setting
    let carouselName = customConfig.carouselName || this.element.dataset.carouselName;
    if (!carouselName) {
      // Try to extract from element classes (e.g., "swiper__carousel reviews" -> "reviews")
      const classes = this.element.classList;
      for (const cls of classes) {
        if (cls && cls !== "swiper__carousel" && !cls.startsWith("swiper")) {
          carouselName = cls;
          break;
        }
      }
    }
    carouselName = carouselName || "carousel";

    // If config has useDefault: true or doesn't explicitly opt out, merge with defaults
    if (customConfig.useDefault !== false) {
      const defaultConfig = getDefaultCarouselConfig(configId, carouselName, customConfig);
      return deepMerge(defaultConfig, customConfig);
    }

    return customConfig;
  }

  /**
   * Update Swiper configuration dynamically
   * @param {Object} newConfig - New configuration to merge
   */
  updateConfig(newConfig) {
    if (!this.swiper) {
      console.warn("Cannot update config: Swiper not initialized");
      return;
    }

    Object.assign(this.swiper.params, newConfig);
    this.swiper.update();
  }

  /**
   * Get the current Swiper instance
   * @returns {any | null} Swiper instance
   */
  getInstance() {
    return this.swiper;
  }

  /**
   * Check if carousel is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this.swiper !== null && this.element.classList.contains("swiper-initialized");
  }

  /**
   * Destroy the carousel instance and clean up
   * @param {boolean} [deleteInstance=true] - Whether to delete the instance
   * @param {boolean} [cleanStyles=true] - Whether to clean up Swiper styles
   */
  destroy(deleteInstance = true, cleanStyles = true) {
    if (this.swiper) {
      this.swiper.destroy(deleteInstance, cleanStyles);
      this.swiper = null;
    }

    // Clean up instance reference
    if (this.element._carouselInstance) {
      delete this.element._carouselInstance;
    }
  }
}

/**
 * Check if an element is in a hidden container (has aria-hidden="true" or inert attribute)
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if element is in a hidden container
 */
function isInHiddenContainer(element) {
  let current = element;
  while (current && current !== document.body) {
    // Check if element itself or ancestor has aria-hidden="true"
    if (current.getAttribute && current.getAttribute("aria-hidden") === "true") {
      return true;
    }
    // Check if element itself or ancestor has inert attribute
    if (current.hasAttribute && current.hasAttribute("inert")) {
      return true;
    }
    current = current.parentElement || null;
  }
  return false;
}

/**
 * Initialize a single carousel element (used for lazy initialization)
 * @param {HTMLElement} element - The carousel element to initialize
 * @returns {CarouselComponent | null} Carousel instance or null if initialization failed
 */
function initSingleCarousel(element) {
  // Skip if already initialized
  if (element.classList.contains("swiper-initialized")) {
    if (element._carouselInstance) {
      return element._carouselInstance;
    }
    return null;
  }

  const carousel = new CarouselComponent(/** @type {HTMLElement} */ (element));
  const swiper = carousel.init();

  if (swiper) {
    return carousel;
  }
  return null;
}

/**
 * Initialize all carousel instances on the page
 * @param {string} [selector=".swiper__carousel"] - CSS selector for carousel elements
 * @param {boolean} [skipHidden=true] - Skip carousels in hidden containers (aria-hidden or inert)
 * @returns {CarouselComponent[]} Array of initialized carousel instances
 */
export function initCarousels(selector = ".swiper__carousel", skipHidden = true) {
  const carouselElements = document.querySelectorAll(selector);
  const instances = [];
  const hiddenCarousels = [];

  for (const element of carouselElements) {
    const htmlElement = /** @type {HTMLElement} */ (element);

    // Skip if already initialized
    if (htmlElement.classList.contains("swiper-initialized")) {
      // Return existing instance if available
      if (htmlElement._carouselInstance) {
        instances.push(htmlElement._carouselInstance);
      }
      continue;
    }

    // Check if carousel is in a hidden container
    if (skipHidden && isInHiddenContainer(htmlElement)) {
      // Store for later initialization when visible
      hiddenCarousels.push(htmlElement);
      continue;
    }

    const carousel = initSingleCarousel(htmlElement);
    if (carousel) {
      instances.push(carousel);
    }
  }

  // Set up IntersectionObserver for hidden carousels
  if (hiddenCarousels.length > 0) {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const target = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting && !target.classList.contains("swiper-initialized")) {
            // Carousel is now visible, initialize it
            initSingleCarousel(target);
            observer.unobserve(target);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.01, // Trigger when any part is visible
      }
    );

    hiddenCarousels.forEach(carousel => {
      observer.observe(/** @type {HTMLElement} */ (carousel));
    });
  }

  return instances;
}

/**
 * Initialize a specific carousel element
 * @param {HTMLElement} element - The swiper container element
 * @param {Object} [config] - Optional Swiper configuration
 * @returns {CarouselComponent | null} Carousel instance or null if initialization failed
 */
export function initCarousel(element, config) {
  if (!element) {
    console.warn("initCarousel: No element provided");
    return null;
  }

  // Return existing instance if already initialized
  if (element._carouselInstance && element._carouselInstance.isInitialized()) {
    return element._carouselInstance;
  }

  const carousel = new CarouselComponent(element, config);
  const swiper = carousel.init();

  return swiper ? carousel : null;
}

/**
 * Destroy all carousel instances on the page
 * @param {string} [selector=".swiper__carousel"] - CSS selector for carousel elements
 */
export function destroyCarousels(selector = ".swiper__carousel") {
  const carouselElements = document.querySelectorAll(selector);

  for (const element of carouselElements) {
    if (element._carouselInstance) {
      element._carouselInstance.destroy();
    }
  }
}

/**
 * Get carousel instance from an element
 * @param {HTMLElement} element - The carousel element
 * @returns {CarouselComponent | null} Carousel instance or null
 */
export function getCarouselInstance(element) {
  return element._carouselInstance || null;
}

// Make functions globally available for inline scripts in sections
window.initCarousel = initCarousel;
window.initCarousels = initCarousels;
window.getCarouselInstance = getCarouselInstance;

// Auto-initialize carousels on page load
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initCarousels();
    // Emit custom event to signal carousel.js is ready
    document.dispatchEvent(new CustomEvent("carousel:ready"));
  });
} else {
  initCarousels();
  // Emit custom event to signal carousel.js is ready
  document.dispatchEvent(new CustomEvent("carousel:ready"));
}

// Handle Shopify theme editor events
document.addEventListener("shopify:section:load", event => {
  // Re-initialize carousels in the loaded section
  const sectionElement = event.target;
  if (sectionElement) {
    const carousels = sectionElement.querySelectorAll(".swiper__carousel");
    carousels.forEach(carousel => {
      if (!carousel.classList.contains("swiper-initialized")) {
        initCarousel(/** @type {HTMLElement} */ (carousel));
      }
    });
  }
});

document.addEventListener("shopify:section:unload", event => {
  // Destroy carousels in the unloaded section
  const sectionElement = event.target;
  if (sectionElement) {
    const carousels = sectionElement.querySelectorAll(".swiper__carousel");
    carousels.forEach(carousel => {
      if (carousel._carouselInstance) {
        carousel._carouselInstance.destroy();
      }
    });
  }
});

/**
 * Deep merge utility for objects
 * @param {Object} target - Target object to merge into
 * @param {Object} source - Source object to merge from
 * @returns {Object} Merged object
 */
function deepMerge(target, source) {
  const output = { ...target };

  if (isObject(target) && isObject(source)) {
    for (const key of Object.keys(source)) {
      // Special handling for 'on' object - compose event handlers
      if (key === "on" && isObject(target[key]) && isObject(source[key])) {
        output[key] = composeEventHandlers(target[key], source[key]);
      } else if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else if (Array.isArray(source[key])) {
        // For arrays, replace entirely (Swiper configs typically need full array replacement)
        output[key] = source[key];
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    }
  }

  return output;
}

/**
 * Compose event handlers from default and custom configs
 * When both have a handler for the same event, compose them so both are called
 * @param {Object} defaultHandlers - Default event handlers object
 * @param {Object} customHandlers - Custom event handlers object
 * @returns {Object} Composed event handlers object
 */
function composeEventHandlers(defaultHandlers, customHandlers) {
  const composed = { ...defaultHandlers };

  // Iterate through all custom handlers
  Object.keys(customHandlers).forEach(eventName => {
    const defaultHandler = defaultHandlers[eventName];
    const customHandler = customHandlers[eventName];

    // If both are functions, compose them (default first, then custom)
    if (typeof defaultHandler === "function" && typeof customHandler === "function") {
      composed[eventName] = function (...args) {
        // Call default handler first to preserve progress bar initialization
        defaultHandler.apply(this, args);
        // Then call custom handler
        customHandler.apply(this, args);
      };
    } else if (customHandler !== undefined) {
      // If only custom exists or default is not a function, use custom
      composed[eventName] = customHandler;
    }
    // If only default exists, it's already in composed from the spread above
  });

  return composed;
}

/**
 * Check if value is a plain object
 * @param {any} item - Value to check
 * @returns {boolean}
 */
function isObject(item) {
  return item && typeof item === "object" && !Array.isArray(item) && item !== null;
}

/**
 * Get default carousel configuration
 * This provides common defaults that can be extended by individual carousels
 * @param {string} configId - Unique identifier for this carousel instance
 * @param {string} carouselName - Name of the carousel (for progress bar updates)
 * @param {Object} customConfig - Custom config to extract values from (for loop, speed, autoplay)
 * @returns {Object} Default Swiper configuration
 */
export function getDefaultCarouselConfig(configId, carouselName, customConfig = {}) {
  const loop = customConfig.loop !== undefined ? customConfig.loop : false;
  const speed = customConfig.speed !== undefined ? customConfig.speed : 500;
  const autoplayEnabled = customConfig.autoplay && customConfig.autoplay !== false;
  const autoplayInterval = customConfig.autoplay?.delay || 5000;
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return {
    resizeObserver: false,
    speed: speed,
    loop: loop,
    a11y: { enabled: false },
    autoplay:
      autoplayEnabled && !prefersReducedMotion
        ? {
            delay: autoplayInterval,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }
        : false,
    navigation: {
      nextEl: `[data-carousel-pagination='${configId}'] .nav-next`,
      prevEl: `[data-carousel-pagination='${configId}'] .nav-prev`,
    },
    pagination: {
      el: `[data-carousel-pagination='${configId}'] .pagination`,
      clickable: true,
      type: "bullets",
      renderBullet: (index, className) => {
        const isActive = className.includes("swiper-pagination-bullet-active");
        return `<button type="button" class="${className}" aria-label="Slide ${index + 1}" aria-current="${isActive ? "true" : "false"}" tabindex="${isActive ? "0" : "-1"}"><span class="progress-bar"></span></button>`;
      },
    },
    on: {
      init: function (swiper) {
        if (typeof window.attachPaginationHandlers === "function") {
          window.attachPaginationHandlers(swiper, `[data-carousel-pagination='${configId}'] .pagination`);
        }

        // Update pagination bullets with proper ARIA attributes and slide IDs
        updatePaginationAria(swiper, configId);

        // Set initial progress bar state based on autoplay
        const autoplayEnabled = swiper.params.autoplay && swiper.params.autoplay !== false;
        if (autoplayEnabled) {
          // Reset to 0% when autoplay is enabled
          if (typeof window.resetProgressBar === "function") {
            window.resetProgressBar(swiper, carouselName, configId);
          }
        } else {
          // Fill to 100% when autoplay is off
          if (typeof window.fillProgressBar === "function") {
            window.fillProgressBar(swiper, carouselName, configId);
          }
        }
      },
      slideChange: function (swiper) {
        // Update pagination ARIA attributes on slide change
        updatePaginationAria(swiper, configId);

        const autoplayEnabled = swiper.params.autoplay && swiper.params.autoplay !== false;

        if (autoplayEnabled) {
          // Reset progress bar to 0% when autoplay is enabled
          // Progress will start after transition completes
          if (typeof window.resetProgressBar === "function") {
            window.resetProgressBar(swiper, carouselName, configId);
          }
        } else {
          // Immediately fill to 100% when autoplay is off
          if (typeof window.fillProgressBar === "function") {
            window.fillProgressBar(swiper, carouselName, configId);
          }
        }
      },
      transitionEnd: function (swiper) {
        const autoplayEnabled = swiper.params.autoplay && swiper.params.autoplay !== false;

        // Only reset progress bar after transition completes when autoplay is enabled
        // This ensures the progress starts from 0% after the slide transition finishes
        if (autoplayEnabled && typeof window.resetProgressBar === "function") {
          window.resetProgressBar(swiper, carouselName, configId);
        }
      },
      autoplayTimeLeft: function (swiper, time, progress) {
        if (typeof window.updateProgressBar === "function") {
          window.updateProgressBar(swiper, progress, carouselName, configId);
        }
      },
    },
  };
}

/**
 * Carousel Utility Functions
 * These functions provide common carousel functionality that can be reused across sections
 */

/**
 * Update pagination bullets with proper ARIA attributes
 * Sets aria-current and aria-label based on active slide
 * @param {any} swiper - Swiper instance
 * @param {string} configId - Carousel configuration ID
 */
function updatePaginationAria(swiper, configId) {
  if (!swiper || !swiper.el || !swiper.slides) return;

  const paginationEl =
    swiper.el
      .closest("[data-section-id], [data-block-id]")
      ?.querySelector(`[data-carousel-pagination='${configId}'] .pagination`) ||
    document.querySelector(`[data-carousel-pagination='${configId}'] .pagination`);

  if (!paginationEl) return;

  const bullets = paginationEl.querySelectorAll(".swiper-pagination-bullet");
  const activeIndex = swiper.realIndex !== undefined ? swiper.realIndex : swiper.activeIndex;
  const totalSlides = bullets.length;

  bullets.forEach((bullet, index) => {
    const isActive = index === activeIndex;

    bullet.setAttribute("aria-current", isActive ? "true" : "false");
    bullet.setAttribute("aria-label", `Slide ${index + 1} of ${totalSlides}`);
    bullet.setAttribute("tabindex", isActive ? "0" : "-1");
  });
}

/**
 * Update progress bar animation for carousel autoplay
 * @param {any} swiper - Swiper instance
 * @param {number} progress - Progress value (0-1)
 * @param {string} name - name of the carousel
 * @param {string} id - Id to use for queries
 */
export function updateProgressBar(swiper, progress, name, id) {
  if (!swiper || !swiper.el) return;

  const wrapper = swiper.el.closest(`.${name}-carousel-wrapper`);
  if (!wrapper) return;

  const activeBullet = wrapper.querySelector(
    `[data-carousel-pagination='${id}'] .swiper-pagination-bullet-active .progress-bar`
  );

  if (activeBullet) {
    const width = (1 - progress) * 100;
    activeBullet.style.width = `${width}%`;
  }
}

/**
 * Reset progress bar to 0% for the active bullet
 * @param {any} swiper - Swiper instance
 * @param {string} name - name of the carousel
 * @param {string} id - Id to use for queries
 */
export function resetProgressBar(swiper, name, id) {
  if (!swiper || !swiper.el) return;

  const wrapper = swiper.el.closest(`.${name}-carousel-wrapper`);
  if (!wrapper) return;

  const activeBullet = wrapper.querySelector(
    `[data-carousel-pagination='${id}'] .swiper-pagination-bullet-active .progress-bar`
  );

  if (activeBullet) {
    activeBullet.style.width = "0%";
  }
}

/**
 * Set progress bar to 100% for the active bullet (when autoplay is off)
 * @param {any} swiper - Swiper instance
 * @param {string} name - name of the carousel
 * @param {string} id - Id to use for queries
 */
export function fillProgressBar(swiper, name, id) {
  if (!swiper || !swiper.el) return;

  const wrapper = swiper.el.closest(`.${name}-carousel-wrapper`);
  if (!wrapper) return;

  const activeBullet = wrapper.querySelector(
    `[data-carousel-pagination='${id}'] .swiper-pagination-bullet-active .progress-bar`
  );

  if (activeBullet) {
    // Disable transition for instant fill when autoplay is off
    activeBullet.style.transition = "none";
    activeBullet.style.width = "100%";
    // Re-enable transition after a brief moment for future updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        activeBullet.style.transition = "";
      });
    });
  }
}

/**
 * Attach pagination click handlers for carousel
 * @param {any} swiper - Swiper instance
 * @param {string} paginationSelector - Selector for the pagination container
 */
export function attachPaginationHandlers(swiper, paginationSelector) {
  if (!swiper || !swiper.el) return;

  const paginationEl =
    swiper.el.closest("[data-section-id]")?.querySelector(paginationSelector) ||
    swiper.el.closest("[data-block-id]")?.querySelector(paginationSelector) ||
    document.querySelector(paginationSelector);

  if (!paginationEl || paginationEl.dataset.handlersAttached) return;

  // Click handler
  paginationEl.addEventListener("click", function (e) {
    const bullet = e.target.closest(".swiper-pagination-bullet");
    if (bullet) {
      const bullets = Array.from(paginationEl.querySelectorAll(".swiper-pagination-bullet"));
      const index = bullets.indexOf(bullet);
      if (index !== -1) {
        if (swiper.params.loop) {
          swiper.slideToLoop(index);
        } else {
          swiper.slideTo(index);
        }
      }
    }
  });

  // Keyboard handler for tab pattern (Arrow keys)
  paginationEl.addEventListener("keydown", function (e) {
    const bullet = e.target.closest(".swiper-pagination-bullet");
    if (!bullet || !bullet.matches("button.swiper-pagination-bullet")) return;

    const bullets = Array.from(paginationEl.querySelectorAll(".swiper-pagination-bullet"));
    const currentIndex = bullets.indexOf(bullet);
    let targetIndex = -1;

    if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      targetIndex = currentIndex > 0 ? currentIndex - 1 : swiper.params.loop ? bullets.length - 1 : 0;
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      targetIndex = currentIndex < bullets.length - 1 ? currentIndex + 1 : swiper.params.loop ? 0 : bullets.length - 1;
    } else if (e.key === "Home") {
      e.preventDefault();
      targetIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      targetIndex = bullets.length - 1;
    }

    if (targetIndex !== -1 && targetIndex !== currentIndex) {
      const targetBullet = bullets[targetIndex];
      if (targetBullet) {
        targetBullet.focus();
        if (swiper.params.loop) {
          swiper.slideToLoop(targetIndex);
        } else {
          swiper.slideTo(targetIndex);
        }
      }
    }
  });

  paginationEl.dataset.handlersAttached = "true";
}

/**
 * Update navigation button visibility based on carousel position and loop setting
 * @param {any} swiper - Swiper instance
 * @param {string} prevSelector - Selector for the previous button
 * @param {string} nextSelector - Selector for the next button
 */
export function updateNavigationVisibility(swiper, prevSelector, nextSelector) {
  if (!swiper || !swiper.el) return;

  // Handle both selector strings and DOM elements
  let prevButton, nextButton;

  if (typeof prevSelector === "string") {
    // Try to find buttons within the swiper's context first, then fall back to document
    const wrapper = swiper.el.closest("[data-section-id]") || swiper.el.closest("[data-block-id]") || document;
    prevButton = wrapper.querySelector(prevSelector);
    nextButton = wrapper.querySelector(nextSelector);

    // If not found in wrapper, try document (for full selectors like #Hero-id)
    if (!prevButton || !nextButton) {
      prevButton = document.querySelector(prevSelector);
      nextButton = document.querySelector(nextSelector);
    }
  } else {
    // Already DOM elements, use them directly
    prevButton = prevSelector;
    nextButton = nextSelector;
  }

  if (!prevButton || !nextButton) return;

  const isLooping = swiper.params.loop === true;

  // If looping is enabled, both buttons are always enabled
  if (isLooping) {
    prevButton.disabled = false;
    nextButton.disabled = false;
    return;
  }

  // When looping is disabled, disable buttons at beginning/end
  if (swiper.isBeginning) {
    prevButton.disabled = true;
  } else {
    prevButton.disabled = false;
  }

  if (swiper.isEnd) {
    nextButton.disabled = true;
  } else {
    nextButton.disabled = false;
  }
}

/**
 * Attach navigation visibility updates to swiper events
 * This function automatically updates navigation buttons on init and slide changes
 * @param {any} swiper - Swiper instance
 * @param {string} prevSelector - Selector for the previous button
 * @param {string} nextSelector - Selector for the next button
 */
export function attachNavigationVisibilityHandlers(swiper, prevSelector, nextSelector) {
  if (!swiper || !swiper.el) return;

  // Update on init
  updateNavigationVisibility(swiper, prevSelector, nextSelector);

  // Update on slide change
  swiper.on("slideChange", () => {
    updateNavigationVisibility(swiper, prevSelector, nextSelector);
  });

  // Update on reach beginning/end (for non-looping carousels)
  swiper.on("reachBeginning", () => {
    updateNavigationVisibility(swiper, prevSelector, nextSelector);
  });

  swiper.on("reachEnd", () => {
    updateNavigationVisibility(swiper, prevSelector, nextSelector);
  });
}

/**
 * Attach keyboard handlers for carousel controls
 * @param {any} swiper - Swiper instance
 * @param {string} controlSelector - Selector for control elements (buttons, pagination, etc.)
 */
export function attachKeyboardHandlers(swiper, controlSelector) {
  if (!swiper || !swiper.el) return;

  const wrapper = swiper.el.closest("[data-section-id]") || swiper.el.closest("[data-block-id]") || document;
  const controls = wrapper.querySelectorAll(controlSelector);

  if (!controls || controls.length === 0) return;

  controls.forEach(control => {
    if (control.dataset.keyboardHandlersAttached) return;

    control.addEventListener("keydown", function (e) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        if (control.matches(".swiper-pagination-bullet")) {
          const paginationEl = control.closest(".swiper-pagination");
          if (paginationEl) {
            const bullets = Array.from(paginationEl.querySelectorAll(".swiper-pagination-bullet"));
            const index = bullets.indexOf(control);
            if (index !== -1) {
              if (swiper.params.loop) {
                swiper.slideToLoop(index);
              } else {
                swiper.slideTo(index);
              }
            }
          }
        } else {
          control.click();
        }
      }
    });

    control.dataset.keyboardHandlersAttached = "true";
  });
}

/**
 * Manage aria-live attribute for carousel announcements
 * @param {string} wrapperSelector - Selector for the carousel wrapper element
 * @param {boolean} isRotating - Whether the carousel is currently rotating/autoplaying
 * @param {any} [swiper] - Optional Swiper instance to find wrapper relative to
 */
export function manageAriaLive(wrapperSelector, isRotating, swiper) {
  let wrapper;
  if (swiper && swiper.el) {
    wrapper = swiper.el.closest(wrapperSelector);
  } else {
    wrapper = document.querySelector(wrapperSelector);
  }

  if (!wrapper) return;

  const carouselContainer = wrapper.querySelector(".swiper__carousel, .swiper");
  if (!carouselContainer) return;

  if (isRotating) {
    carouselContainer.setAttribute("aria-live", "off");
  } else {
    carouselContainer.setAttribute("aria-live", "polite");
  }
}

/**
 * Attach pause/resume handlers for carousel autoplay on hover and focus
 * @param {any} swiper - Swiper instance
 * @param {string} wrapperSelector - Selector for the carousel wrapper element
 */
export function attachPauseResumeFocusHandlers(swiper, wrapperSelector) {
  if (!swiper || !swiper.el) return;

  const wrapper = swiper.el.closest(wrapperSelector);
  if (!wrapper || wrapper.dataset.pauseResumeHandlersAttached) return;

  const handlePause = () => {
    if (swiper.autoplay && swiper.autoplay.running) {
      swiper.autoplay.pause();
      manageAriaLive(wrapperSelector, false, swiper);
    }
  };

  const handleResume = () => {
    if (swiper.autoplay && swiper.autoplay.paused) {
      swiper.autoplay.resume();
      manageAriaLive(wrapperSelector, true, swiper);
    }
  };

  wrapper.addEventListener("mouseenter", handlePause);
  wrapper.addEventListener("mouseleave", handleResume);
  wrapper.addEventListener("focusin", handlePause);
  wrapper.addEventListener("focusout", handleResume);

  wrapper.dataset.pauseResumeHandlersAttached = "true";
}

/**
 * Reinitialize pagination for all carousels within a container
 * Useful when carousels are in hidden containers that become visible
 * @param {HTMLElement} container - Container element to search for carousels
 */
export function reinitializeCarouselPagination(container) {
  if (!container) return;

  const carousels = container.querySelectorAll(".swiper__carousel");
  carousels.forEach(carouselElement => {
    // Get the carousel instance
    const carouselInstance = carouselElement._carouselInstance;
    if (!carouselInstance || !carouselInstance.swiper) {
      console.warn("Carousel instance or Swiper not found for:", carouselElement);
      return;
    }

    const swiper = carouselInstance.swiper;

    // Check if pagination is configured in params
    if (!swiper.params || !swiper.params.pagination) {
      console.warn("Pagination not configured for carousel:", carouselElement);
      return;
    }

    // Get the pagination selector from params
    const paginationSelector = swiper.params.pagination.el;
    if (!paginationSelector) {
      console.warn("Pagination selector not found in params:", carouselElement);
      return;
    }

    // Find the pagination element
    const paginationEl =
      typeof paginationSelector === "string" ? document.querySelector(paginationSelector) : paginationSelector;

    if (!paginationEl) {
      console.warn("Pagination element not found:", paginationSelector, carouselElement);
      return;
    }

    // Check if bullets exist
    const bullets = paginationEl.querySelectorAll(".swiper-pagination-bullet");
    const slideCount = swiper.slides ? swiper.slides.length : 0;

    if (bullets.length === 0 && slideCount > 0) {
      // Try to use Swiper's pagination.render if available
      if (swiper.pagination && typeof swiper.pagination.render === "function") {
        // Set pagination.el if it's not set
        if (!swiper.pagination.el) {
          swiper.pagination.el = paginationEl;
        }
        swiper.pagination.render();
        swiper.pagination.update();
      } else {
        // Manually create bullets if Swiper's pagination module isn't available
        const renderBullet =
          swiper.params.pagination.renderBullet ||
          ((index, className) => {
            return `<button type="button" class="${className}" aria-label="Slide ${index + 1}" aria-current="false" tabindex="-1"><span class="progress-bar"></span></button>`;
          });

        // Clear existing content
        paginationEl.innerHTML = "";

        // Get real slide count (accounting for loop duplicates)
        const realSlideCount =
          swiper.params.loop && swiper.slides
            ? Math.floor(swiper.slides.length / (swiper.loopedSlides || 1))
            : slideCount;

        // Create bullets for each slide
        for (let i = 0; i < realSlideCount; i++) {
          const className =
            i === swiper.realIndex
              ? "swiper-pagination-bullet swiper-pagination-bullet-active"
              : "swiper-pagination-bullet";
          const bulletHTML =
            typeof renderBullet === "function"
              ? renderBullet(i, className)
              : `<button type="button" class="${className}" aria-label="Slide ${i + 1}" aria-current="${i === swiper.realIndex ? "true" : "false"}" tabindex="${i === swiper.realIndex ? "0" : "-1"}"><span class="progress-bar"></span></button>`;

          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = bulletHTML;
          const bullet = tempDiv.firstElementChild;

          if (bullet) {
            bullet.addEventListener("click", () => {
              if (swiper.params.loop) {
                swiper.slideToLoop(i);
              } else {
                swiper.slideTo(i);
              }
            });
            paginationEl.appendChild(bullet);
          }
        }

        // Attach pagination handlers
        if (typeof window.attachPaginationHandlers === "function") {
          window.attachPaginationHandlers(swiper, paginationSelector);
        }

        // Extract configId from pagination selector or carousel element
        let configId = "";
        if (typeof paginationSelector === "string") {
          const match = paginationSelector.match(/data-carousel-pagination=['"]([^'"]+)['"]/);
          if (match && match[1]) {
            configId = match[1];
          }
        }

        // If not found in selector, try to get from carousel config
        if (!configId && carouselInstance.config) {
          configId = carouselInstance.config.configId || "";
        }

        // Get carousel name from config or default
        const carouselName = carouselInstance.config?.carouselName || "featured-callout";

        // Set initial progress bar state
        const autoplayEnabled = swiper.params.autoplay && swiper.params.autoplay !== false;
        if (autoplayEnabled) {
          if (typeof window.resetProgressBar === "function") {
            window.resetProgressBar(swiper, carouselName, configId);
          }
        } else {
          if (typeof window.fillProgressBar === "function") {
            window.fillProgressBar(swiper, carouselName, configId);
          }
        }
      }
    }
  });
}

// Make utility functions globally available for inline scripts in sections
window.updateProgressBar = updateProgressBar;
window.resetProgressBar = resetProgressBar;
window.fillProgressBar = fillProgressBar;
window.attachPaginationHandlers = attachPaginationHandlers;
window.updateNavigationVisibility = updateNavigationVisibility;
window.attachNavigationVisibilityHandlers = attachNavigationVisibilityHandlers;
window.attachKeyboardHandlers = attachKeyboardHandlers;
window.manageAriaLive = manageAriaLive;
window.attachPauseResumeFocusHandlers = attachPauseResumeFocusHandlers;
window.getDefaultCarouselConfig = getDefaultCarouselConfig;
window.reinitializeCarouselPagination = reinitializeCarouselPagination;

// Export the component class for advanced usage
export { CarouselComponent };
